import { Contract } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  Grid,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
} from '@chakra-ui/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import HappyFace from '../assets/happy_face.svg';
import SadFace from '../assets/sad_face.svg';
import USAFlagImage from '../assets/usa_flag.png';
import CancelBetModal from '../components/CancelBetModal';
import { BidenFace, TrumpFace } from '../components/CandidateFaces';
import Header from '../components/Header';
import ResolutionDetailsModal from '../components/ResolutionDetailsModal';
import WalletUnlockModal from '../components/WalletUnlockModal';
import WithdrawWinningsModal from '../components/WithdrawWinningsModal';
import { getTowerService } from '../services/towerService';
import { getTwitterInfoById } from '../services/twitter';
import { useAppDispatch } from '../store';
import {
  fetchBetState,
  hydrateBets,
  performBetAction,
} from '../store/bets/betsSlice';
import { RootState } from '../store/reducers';
import { ELECTION_BETTING_END_DATE } from '../utils/Election';
import { round, toBTC } from '../utils/Utils';

const ActionButton = ({ label, action, ...props }) => (
  <Button
    h="100%"
    whiteSpace="normal"
    colorScheme="blue"
    onClick={action}
    {...props}
  >
    {label}
  </Button>
);

const CancelButton = ({ label, action, ...props }) => (
  <Button
    h="100%"
    whiteSpace="normal"
    colorScheme="white"
    onClick={action}
    {...props}
  >
    {label}
  </Button>
);

const DummyButton = ({ label, ...props }) => (
  <Button
    h="100%"
    pointerEvents="none"
    whiteSpace="normal"
    bg="#707070"
    color="white"
    {...props}
  >
    {label}
  </Button>
);

interface ParamTypes {
  contractUUID: string;
}

const BetPartyCard = (props) => {
  const { heading, image, name, amount, ..._props } = props;

  const btcPrice = useSelector((state: RootState) => state.prices.btcusd);

  const satsToValue = (sats) =>
    round((sats / 100000000) * btcPrice, 2).toFixed(2);

  return (
    <Box
      textAlign="center"
      p={4}
      w="100%"
      backgroundColor="#ffffff"
      borderRadius="8px"
      {..._props}
    >
      <Heading as="h4" size="md">
        {heading}
      </Heading>
      <Image
        boxSize="68px"
        borderRadius="100%"
        margin="0 auto"
        mt={4}
        src={image}
      />
      <Text mt={2}>{name} Deposited</Text>
      <Text>
        <b>{toBTC(amount)} BTC</b>{' '}
        <Text as="span" color="#a0a0a0">
          ${satsToValue(amount)} USD
        </Text>
      </Text>
    </Box>
  );
};

const ViewBet = () => {
  const { contractUUID } = useParams<ParamTypes>();

  const history = useHistory();
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);
  const bet = useSelector((state: RootState) => state.bets[contractUUID]);

  const [fetched, setFetched] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [twitterInfo, setTwitterInfo] = useState<any | undefined>();
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!mnemonic) return;

    (async () => {
      const twitterId = await getTowerService().findTwitterId(contractUUID);
      const _twitterInfo = await getTwitterInfoById(twitterId);

      setTwitterInfo(_twitterInfo);
    })();

    dispatch(hydrateBets())
      .then(() => dispatch(fetchBetState({ id: contractUUID, utxos: [] })))
      // .then(() => dispatch(importBet(contractUUID)))
      .then(() => setFetched(true));
  }, [contractUUID, dispatch, mnemonic]);

  if (!mnemonic) return <WalletUnlockModal />;
  if (!bet) return <p>Loading...</p>;

  const localPrediction =
    bet.contract.outcomes[0].message === 'democratic'
      ? 'Democratic'
      : 'Republican';
  const remotePrediction =
    bet.contract.outcomes[0].message === 'democratic'
      ? 'Republican'
      : 'Democratic';

  const contract = Contract.fromJSON(bet.contract);
  console.log(contract);

  console.log(bet);
  const renderLocalAction = () => {
    if (
      bet.stage === 'OFFER' &&
      !!bet.towerInfo?.accept &&
      new Date() < ELECTION_BETTING_END_DATE
    ) {
      return (
        <>
          <ActionButton
            label="Confirm Bet"
            isLoading={isPerformingAction}
            loadingText="Confirming Bet..."
            action={() => {
              setIsPerformingAction(true);
              dispatch(performBetAction(bet.contractId)).then(() =>
                setIsPerformingAction(false)
              );
            }}
            gridArea="lb"
          />
          {renderCancelButton('lb', true)}
        </>
      );
    }
    if (bet.closeInfo && bet.closeInfo.withdrawable && leftWin) {
      return (
        <ActionButton
          label="Withdraw Winnings"
          gridArea="lb"
          action={() => setShowWithdrawModal(true)}
        />
      );
    }
    if (
      (bet.stage === 'OFFER' && !bet.towerInfo?.accept) ||
      bet.stage === 'SIGN' ||
      (bet.contract.isLocalParty &&
        new Date() > ELECTION_BETTING_END_DATE &&
        bet.stage !== 'ACTIVE')
    ) {
      return renderCancelButton('lb');
    }
    if (
      bet.contract.isLocalParty &&
      bet.stage === 'CANCELLED' &&
      bet.withdrawable
    ) {
      return renderWithdrawButton('lb');
    }
    return <></>;
  };

  const renderRemoteAction = () => {
    if (bet.contract.isLocalParty && bet.towerInfo?.accept) {
      switch (bet.stage) {
        case 'OFFER':
          return (
            <DummyButton gridArea="rb" label="Counterparty has deposited" />
          );
      }
    }

    if (
      (bet.stage === 'ACCEPT' && !bet.towerInfo?.sign) ||
      (!bet.contract.isLocalParty &&
        new Date() > ELECTION_BETTING_END_DATE &&
        bet.stage !== 'ACTIVE')
    ) {
      return renderCancelButton('rb');
    }

    if (
      bet.stage === 'ACCEPT' &&
      bet.towerInfo?.sign &&
      new Date() < ELECTION_BETTING_END_DATE
    ) {
      return (
        <>
          <ActionButton
            label="Finalize Bet"
            isLoading={isPerformingAction}
            loadingText="Finalizing Bet..."
            action={() => {
              setIsPerformingAction(true);
              dispatch(performBetAction(bet.contractId)).then(() =>
                setIsPerformingAction(false)
              );
            }}
            gridArea="rb"
          />
          {renderCancelButton('rb', true)}
        </>
      );
    }
    if (bet.closeInfo && bet.closeInfo.withdrawable && !leftWin) {
      return (
        <ActionButton
          label="Withdraw Winnings"
          gridArea="rb"
          action={() => setShowWithdrawModal(true)}
        />
      );
    }
    if (
      !bet.contract.isLocalParty &&
      bet.stage === 'CANCELLED' &&
      bet.withdrawable
    ) {
      return renderWithdrawButton('rb');
    }
    return <></>;
  };

  const renderCancelButton = (align, margin?) => (
    <CancelButton
      label="Cancel Bet"
      action={() => setShowCancelModal(true)}
      gridArea={align}
      marginTop={margin ? '68px' : 0}
      background={'none'}
      color={'#6772e5'}
    />
  );

  const renderWithdrawButton = (align, margin?) => (
    <CancelButton
      label="Withdraw Funds"
      action={() => setShowCancelModal(true)}
      gridArea={align}
      marginTop={margin ? '68px' : 0}
      background={'none'}
      color={'#6772e5'}
    />
  );

  if (!fetched) {
    return (
      <Modal isOpen={true} onClose={() => null} isCentered>
        <ModalOverlay>
          <ModalContent>
            <ModalBody textAlign="center" p={4}>
              <CircularProgress
                // @ts-ignore
                isIndeterminate
                thickness="3px"
                size="80px"
                color="black"
              />

              <Text whiteSpace="nowrap" fontSize="lg" fontWeight="bold" mt={4}>
                Fetching bet details...
              </Text>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    );
  }

  let leftWin;
  if (bet.closeInfo && bet.closeInfo.outcomeIndex !== -1)
    leftWin =
      (bet.closeInfo.isWinner && contract.isLocalParty) ||
      (!bet.closeInfo.isWinner && !contract.isLocalParty);

  return (
    <Box w="100%">
      {bet.closeInfo && (
        <WithdrawWinningsModal
          onClose={() => setShowWithdrawModal(false)}
          isOpen={showWithdrawModal}
          contractId={contractUUID}
        />
      )}

      <CancelBetModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        contractId={contractUUID}
      />
      <ResolutionDetailsModal
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      />
      <Header h="180px" w="100%" alignItems="center" justifyContent="center">
        <Heading color="white" size="lg" textAlign="center">
          US Presidential Election on November 3rd 2020
        </Heading>
        <Button
          bg="white"
          color="blue.600"
          borderColor="blue.600"
          borderWidth="1px"
          position="absolute"
          top="5"
          right="5"
          size="sm"
          colorScheme="white"
          onClick={() => history.push('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Header>
      <Box margin="0 auto" p={16} w="fit-content">
        <Grid
          gridTemplateRows="1fr 200px 60px"
          gridTemplateColumns="400px 200px 400px"
          gridTemplateAreas={`"t1 t2 t3" "lp cn rp" "lb . rb"`}
          w="100%"
          maxWidth="1600px"
          gap="10px"
        >
          {bet.closeInfo && bet.closeInfo.outcomeIndex !== -1 ? (
            <>
              <Box gridArea={leftWin ? 't1' : 't3'} textAlign="center">
                <Image src={HappyFace} mx="auto" mb={2} />
                <Text mb={1}>Won the bet</Text>
              </Box>

              <Box gridArea={leftWin ? 't3' : 't1'} textAlign="center">
                <Image src={SadFace} mx="auto" mb={2} />
                <Text mb={1}>Lost the bet</Text>
              </Box>
            </>
          ) : (
            <>
              <BidenFace
                h="120px"
                gridArea={localPrediction === 'Democratic' ? 't1' : 't3'}
                direction={localPrediction === 'Democratic' ? 'right' : 'left'}
                justifySelf="center"
                // justifySelf={localPrediction === 'Democratic' ? 'start' : 'end'}
              />
              <Box gridArea="t2" justifySelf="center" textAlign="center">
                <Image src={USAFlagImage} h="100px" mb={2} mx="Auto" />
                <Text fontWeight="500">Resolution Details</Text>
                <Text
                  fontWeight="500"
                  fontSize="sm"
                  color="blue.600"
                  textDecor="underline"
                  onClick={onOpen}
                  cursor="pointer"
                >
                  View Details
                </Text>
              </Box>
              <TrumpFace
                h="120px"
                gridArea={localPrediction === 'Democratic' ? 't3' : 't1'}
                direction={localPrediction === 'Democratic' ? 'left' : 'right'}
                justifySelf="center"
                // justifySelf={localPrediction === 'Democratic' ? 'end' : 'start'}
              />
            </>
          )}

          <BetPartyCard
            heading={`${localPrediction} Party Wins`}
            name={contract.isLocalParty ? 'You' : 'Counterparty'}
            image={twitterInfo?.profile_image_url_https?.replace(
              'normal',
              '200x200'
            )}
            amount={contract.localCollateral.GetSatoshiAmount()}
            gridArea="lp"
          />
          <Flex
            textAlign="center"
            alignItems="center"
            justifyContent="center"
            gridArea="cn"
          >
            <Stat>
              <StatLabel>
                {bet.closeInfo?.isWinner ? 'Total Winnings' : 'Total Bet Size'}
              </StatLabel>
              <StatNumber>
                {toBTC(
                  contract.localCollateral.GetSatoshiAmount() +
                    contract.remoteCollateral.GetSatoshiAmount()
                )}{' '}
                BTC
              </StatNumber>
            </Stat>
          </Flex>
          <BetPartyCard
            heading={`${remotePrediction} Party Wins`}
            name={contract.isLocalParty ? 'Counterparty' : 'You'}
            image="https://www.netclipart.com/pp/m/147-1479847_icone-contact-png-user-silhouette-icon.png"
            amount={contract.remoteCollateral.GetSatoshiAmount()}
            gridArea="rp"
          />

          {renderLocalAction()}
          {renderRemoteAction()}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewBet;
